import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiClientConfig } from '../api-client-config';
import { AppDependency, PriorityPatchRequest } from '@primatoo-ms-organization';
import { DataProtectionGovernanceService } from '../interface/data-protection-governance-service';
import { ListApplicationForMenuResponse } from 'it/primatoo/openapi/api/org/schemas/application/ListApplicationForMenuResponse';
import { GetApplicationForDetailsResponse } from 'it/primatoo/openapi/api/org/schemas/application/getapplicationfordetailsresponse/GetApplicationForDetailsResponse';
import { UpdateApplicationRequest } from 'it/primatoo/openapi/api/org/schemas/application/UpdateApplicationRequest';
import { CreateApplicationRequest } from 'it/primatoo/openapi/api/org/schemas/application/CreateApplicationRequest';
import { CreateResponse } from 'it/primatoo/openapi/api/common/schemas/CreateResponse';
import { SecurityCfg } from 'it/primatoo/openapi/api/org/schemas/application/getsecurityconfigdetailsresponse/SecurityCfg';
import { UpdateAppSecurityConfigRequest } from 'it/primatoo/openapi/api/org/schemas/application/updatesecurityconfigrequest/UpdateAppSecurityConfigRequest';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import { Application } from 'it/primatoo/openapi/api/org/schemas/application/Application';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService extends DataProtectionGovernanceService {
  private readonly endpoint: string;
  private readonly endpointV2: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    public httpClient: HttpClient
  ) {
    super(httpClient);
    this.endpoint = this.config.endPoint + '/api/org/applications/';
    this.endpointV2 = this.config.endPoint + '/api/org/v2/applications/';
  }

  // API V2 ============================================================================================================
  public getDetails(id: number): Observable<GetApplicationForDetailsResponse> {
    const url = this.endpointV2 + id + '/details';
    return this.httpClient.get<GetApplicationForDetailsResponse>(url);
  }

  public create(
    requestBody: CreateApplicationRequest
  ): Observable<CreateResponse> {
    const url = this.endpointV2;
    return this.httpClient.post<CreateResponse>(url, requestBody);
  }

  public uploadBom(appId: number, file: File): Observable<any> {
    const url = this.endpointV2 + `${appId}/security-config/bom/upload`;
    const formData = new FormData();
    if (file) {
      formData.append('file', file, file.name);
    }
    const uploadRequest = new HttpRequest('POST', url, formData, {
      reportProgress: true,
    });
    return this.httpClient.request<void>(uploadRequest);
  }

  public getSecurityConfig(appId: number): Observable<SecurityCfg> {
    const url = this.endpointV2 + `${appId}/security-config/`;
    return this.httpClient.get<SecurityCfg>(url);
  }

  public updateSecurityConfig(
    appId: number,
    request: UpdateAppSecurityConfigRequest
  ): Observable<void> {
    const url = this.endpointV2 + `${appId}/security-config/`;
    return this.httpClient.put<void>(url, request);
  }

  public update(
    id: number,
    requestBody: UpdateApplicationRequest
  ): Observable<void> {
    const url = this.endpointV2 + id + '/';
    return this.httpClient.put<void>(url, requestBody);
  }

  public getMenu(): Observable<ListApplicationForMenuResponse> {
    const url = this.endpointV2 + `menu/`;
    return this.httpClient.get<ListApplicationForMenuResponse>(url);
  }

  // API V1 ============================================================================================================
  public findAll(): Observable<Application[]> {
    const url = this.endpoint;
    return this.httpClient.get<Application[]>(url);
  }

  public setPriority(
    id: number,
    request: PriorityPatchRequest
  ): Observable<Application> {
    const url = `${this.endpoint}${id}/priority`;
    return this.httpClient.patch<Application>(url, request);
  }

  public findUserColumns(): Observable<ColumnWithLocalization[]> {
    const url = this.endpoint + `columns`;
    return this.httpClient.get<ColumnWithLocalization[]>(url);
  }

  public getApplicationDependency(id: number): Observable<AppDependency> {
    const url = this.endpoint + id + '/dependency';
    return this.httpClient.get<AppDependency>(url, {});
  }

  public getEndPointV1(): string {
    return this.endpoint;
  }

  public exportRaw() {
    const url = this.endpoint + `download`;
    return this.httpClient.get(url, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
